import type { FC } from "react";
import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";

import { keywordGroupsTransforms } from "app/services/keyword-groups";
import type { IWidgetModelTypesType } from "app/components/widget/widget-types/Widget";
import DurationService, { BasicDurations } from "app/services/DurationService";

import { DataRow } from "../components/datarow/DataRow";
import { KeywordsOverviewPageTopMetricContainer } from "../KeywordsOverviewPageStyledComponents";

import { voulmeClicksRowsConfig } from "app/common/keyword-research/keywords-overview/volumeAndClicksConsts";
import { EVolumeAndClicksMetric } from "app/common/keyword-research/keywords-overview/volumeAndClicksTypes";
import {
    fetchData,
    getApiParams,
    transformClicksData,
    transformVolumeAndNoClicksData,
} from "app/common/keyword-research/keywords-overview/volumeAndClicksUtils";

export const VolumeAndClicks: FC = () => {
    const { params } = useCurrentStateAndParams();
    const { keyword, duration, country, webSource } = params;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [data, setData] = useState<any>({
        volumeRowData: { value: null, change: null },
        clicksRowData: { value: null, change: null },
        zeroClicksRowData: { value: null, change: null },
    });

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const durationObject = DurationService.getDurationData(duration);
            const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);
            const keys = isKeywordGroup ? keyword.substring(1) : keyword;
            const { clicksParams, volumeAndNoClicksParams } = await getApiParams(
                isKeywordGroup,
                keys,
                durationObject.forAPI,
                country,
                webSource,
            );

            try {
                const { volumeAndNoClicksData, clicksData } = await fetchData(volumeAndNoClicksParams, clicksParams);

                const transformedVolumeAndNoClicksData = transformVolumeAndNoClicksData(
                    volumeAndNoClicksData,
                    keys,
                    durationObject.raw.from,
                    webSource,
                    duration !== BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                );
                const transformedClicksData = transformClicksData(
                    clicksData,
                    keys,
                    webSource,
                    durationObject.raw.from,
                    duration !== BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                );
                setData({ ...transformedVolumeAndNoClicksData, ...transformedClicksData });
            } catch (error) {
                Sentry.error(`Error fetching volume and clicks data for keyword analysis overview`, error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    const addToDashboardConfig = {
        metric: "KeywordOverviewPerformanceStats",
        type: "KeywordOverviewStats" as IWidgetModelTypesType,
        webSource,
    };

    return (
        <KeywordsOverviewPageTopMetricContainer
            data-automation="VolumeAndClicks"
            height="300px"
            padding="0"
            width="auto"
        >
            <DataRow
                key={EVolumeAndClicksMetric.VOLUME}
                {...voulmeClicksRowsConfig.find((row) => row.id === EVolumeAndClicksMetric.VOLUME)}
                {...data.volumeRowData}
                isLoading={isLoading}
                addToDashboardConfig={addToDashboardConfig}
            />
            <DataRow
                key={EVolumeAndClicksMetric.CLICKS}
                {...voulmeClicksRowsConfig.find((row) => row.id === EVolumeAndClicksMetric.CLICKS)}
                {...data.clicksRowData}
                isLoading={isLoading}
            />
            <DataRow
                key={EVolumeAndClicksMetric.ZERO_CLICKS}
                {...voulmeClicksRowsConfig.find((row) => row.id === EVolumeAndClicksMetric.ZERO_CLICKS)}
                {...data.zeroClicksRowData}
                isLoading={isLoading}
                invertChangeColor={true}
            />
        </KeywordsOverviewPageTopMetricContainer>
    );
};
